import { lockBodyScroll } from "@/lib/body-scroll-lock";
import { useEffect, useId, useRef, type ReactNode } from "react";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
  size?: "sm" | "md" | "lg" | "xl" | "2xl" | "full" | "drawer" | "workspace";
  /** Keep header/footer visible and scroll the body (stock drawer, tall forms). */
  scrollBody?: boolean;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

const SIZE_MAP: Record<string, string> = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-3xl",
  "2xl": "max-w-4xl",
  full: "max-w-6xl",
  drawer: "max-w-5xl",
  workspace: "max-w-[min(96vw,1400px)]",
};

export function Modal({ open, onClose, title, children, footer, wide, size, scrollBody }: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  // Hold the latest onClose in a ref so the focus/keyboard effect below does
  // NOT need to list it as a dependency. Callers commonly pass inline callbacks
  // (e.g. onClose={() => setOpen(false)}), whose identity changes on every
  // parent render. If that callback were a dependency, each keystroke inside a
  // controlled input would tear down and rebuild the effect: the cleanup would
  // restore focus to the pre-open element (stealing focus away from the field
  // the user is typing in) and the re-run would refocus the first control.
  // Keeping the latest callback in a ref makes the effect stable for the full
  // lifetime of the open dialog.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const widthClass = wide ? "max-w-3xl" : size ? SIZE_MAP[size] ?? "max-w-lg" : "max-w-lg";
  const drawer = size === "drawer" || scrollBody;

  useEffect(() => {
    if (!open) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;

    // Move focus into the dialog when it opens.
    const panel = panelRef.current;
    const focusables = panel ? Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)) : [];
    (focusables[0] ?? panel)?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (e.key !== "Tab" || !panel) return;
      const items = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null,
      );
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKey);
    const releaseScroll = lockBodyScroll();
    return () => {
      document.removeEventListener("keydown", onKey);
      releaseScroll();
      previouslyFocused?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 md:p-8"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div className="fixed inset-0 bg-black/40" onClick={onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        tabIndex={-1}
        className={`relative w-full ${widthClass} bg-white rounded-2xl border border-slate-200 shadow-xl my-4 md:my-8 ${
          drawer ? "flex flex-col max-h-[90vh] min-h-[50vh] h-[min(85vh,920px)] resize-y overflow-hidden" : ""
        }`}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0">
          <h3 id={titleId} className="text-base font-semibold text-slate-900">
            {title}
          </h3>
          <button
            onClick={onClose}
            aria-label="Close dialog"
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 transition-colors"
          >
            <i className="ri-close-line text-slate-500" aria-hidden="true" />
          </button>
        </div>
        <div className={`px-6 py-5 ${drawer ? "flex-1 min-h-0 overflow-y-auto" : ""}`}>{children}</div>
        {footer && (
          <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2 shrink-0 bg-white">{footer}</div>
        )}
      </div>
    </div>
  );
}